import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  HStack,
  Heading,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
import {ChevronLeftIcon, ChevronRightIcon} from '@chakra-ui/icons';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {FiltresItemCatalogue, useCategorieWeb} from 'ff-common';
import useBreadCrumbs from '@/hooks/catalogue/useBreadCrumbs';
import {useClient} from '@/haligator-factory';
import {HaliRenderOne} from '@synako/haligator';
import {imageOptimisee} from '@/domains/mediatheque/optimisation';
import {useTranslation} from 'react-i18next';
import {CatalogHeaderCategoryFiltersBar} from './CatalogHeaderCategoryFiltersBar';
import {HookUseTypedFilters} from '@synako/typed-path';

// Texte posé sur la photo d'ambiance (ambiance promo ou MP) : sombre, détouré de blanc pour rester lisible
const getTextColor = () => {
  return 'black.text';
};

const getTextShadow = () => {
  return '2px 2px 10px white, 0px 0px 10px white, 0px 0px 15px white';
};

// Le bandeau ne montre qu'environ 15 % de la hauteur de la photo, centrée par défaut. Sur cette
// photo (métier A2), le centre montre des torses et les assiettes sont plus bas. Le réglage vise
// le fichier, pas le métier : un client A2 de la 351 ou de la 442 a une autre photo.
const PHOTO_A_CALER_VERS_LE_BAS = '/restauration_commerciale_2.webp';

interface CatalogHeaderProps {
  catWeb: string;
  filtersHook?: HookUseTypedFilters<FiltresItemCatalogue>;
  facets?: Record<string, {values: Record<string, number>}>;
}

export const CatalogHeader: React.FC<CatalogHeaderProps> = ({
  catWeb,
  filtersHook,
  facets,
}) => {
  // split pour récupérer l'image
  const idCategoriePrincipale = catWeb.split('-')[0];
  const navigate = useNavigate();
  const {t} = useTranslation();
  const client = useClient();
  const categorie = useCategorieWeb(catWeb);
  const crumbs = useBreadCrumbs(catWeb);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPromo = searchParams.get('from') === 'promo';
  const fromMP = searchParams.get('from') === 'mp';

  type CategorieId =
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6'
    | '7'
    | '8'
    | '9'
    | '10'
    | '11'
    | '12'
    | '170';

  // gestion des couleurs de fond de header
  const getCategoryInfo = (categorieP: string) => {
    const LIGHT = {
      breadcrumbTextColor: 'blue.text',
      headingTextColor: 'black.text',
    };
    const DARK = {breadcrumbTextColor: 'white', headingTextColor: 'white'};
    const categories = {
      '1': {bgColor: 'categories.frais', ...LIGHT},
      '2': {bgColor: 'categories.epicerie', ...LIGHT},
      '3': {bgColor: 'categories.boissons', ...DARK},
      '4': {bgColor: 'categories.frais', ...LIGHT},
      '5': {bgColor: 'categories.produits_laitiers', ...LIGHT},
      '6': {bgColor: 'categories.epicerie', ...LIGHT},
      '7': {bgColor: 'categories.fruits_et_legumes', ...DARK},
      '8': {bgColor: 'categories.surgeles', ...DARK},
      '9': {bgColor: 'categories.epicerie', ...LIGHT},
      '10': {bgColor: 'categories.non_alimentaire', ...LIGHT},
      '11': {bgColor: 'categories.maree_fraiche', ...DARK},
      '12': {bgColor: 'categories.frais', ...LIGHT},
      '170': {bgColor: 'categories.boissons', ...DARK},
    };

    type CategorieInfo = (typeof categories)[CategorieId];

    return (
      (categories as Record<string, CategorieInfo>)[categorieP] ??
      categories['5']
    );
  };

  const {bgColor, breadcrumbTextColor, headingTextColor} = getCategoryInfo(
    idCategoriePrincipale,
  );

  return (
    <Box
      position='relative'
      p={{base: '0px', md: '20px'}}
      borderRadius='12px'
      mx={{base: '2', md: '4', lg: '6'}}
      overflow='hidden'
      bg={fromPromo || fromMP ? 'transparent' : bgColor}
    >
      {/* Image de fond pour les catégories principales, uniquement si on n'est pas déjà dans une ambiance promo ou MP */}
      {!fromPromo && !fromMP && (
        <Box
          position='absolute'
          right='0'
          top='0'
          bottom='0'
          maxWidth='747px'
          pb={2}
          display={{base: 'none', md: 'flex'}}
          justifyContent='flex-end'
          alignItems='center'
          zIndex={0}
          pointerEvents='none'
        >
          <Image
            onError={(e) =>
              (e.currentTarget.src = `${import.meta.env.BASE_URL}/categories/unknown.webp`)
            }
            src={`${import.meta.env.BASE_URL}/categories/${idCategoriePrincipale}.webp`}
            height='100%'
            objectFit='contain'
          />
        </Box>
      )}
      <HStack
        justify='space-between'
        position='relative'
        zIndex={1}
      >
        {/* Version desktop */}
        <>
          {/* Fil d'Ariane et titre */}
          <VStack
            align='left'
            justifyContent='space-between'
            height='100px'
            paddingLeft='20px'
            display={{base: 'none', md: 'flex'}}
          >
            <Breadcrumb
              spacing='8px'
              mt={2}
              separator={
                <ChevronRightIcon
                  color={
                    fromPromo || fromMP ? getTextColor() : breadcrumbTextColor
                  }
                  opacity='0.8'
                />
              }
            >
              {crumbs.map((crumb, index) => (
                <BreadcrumbItem key={index}>
                  <BreadcrumbLink
                    as={Link}
                    to={crumb.link}
                    opacity={fromPromo || fromMP ? '1' : '0.8'}
                    fontWeight={index === crumbs.length - 1 ? '700' : '400'}
                    fontSize='sm'
                    lineHeight='17px'
                    color={
                      fromPromo || fromMP ? getTextColor() : breadcrumbTextColor
                    }
                    textShadow={fromPromo || fromMP ? getTextShadow() : 'none'}
                  >
                    {crumb.name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              ))}
              {fromPromo && (
                <BreadcrumbItem>
                  <Text
                    fontWeight='700'
                    fontSize='sm'
                    color={getTextColor()}
                    textShadow={getTextShadow()}
                  >
                    {t('catalogue.les_promotions')}
                  </Text>
                </BreadcrumbItem>
              )}
              {fromMP && (
                <BreadcrumbItem>
                  <Text
                    fontWeight='700'
                    fontSize='sm'
                    color={getTextColor()}
                    textShadow={getTextShadow()}
                  >
                    {t('catalogue.les_indispensables')}
                  </Text>
                </BreadcrumbItem>
              )}
            </Breadcrumb>
            <Heading
              as='h2'
              fontSize='3xl'
              fontWeight='700'
              color={headingTextColor}
              mr='4'
            >
              {categorie?.libelle}
            </Heading>
          </VStack>
        </>
      </HStack>
      {/* Version Mobile */}
      <Box
        display={{base: 'flex', md: 'none'}}
        p={4}
      >
        <HStack
          onClick={() => {
            navigate(-1);
          }}
          cursor='pointer'
        >
          <ChevronLeftIcon
            boxSize='25px'
            color={fromPromo || fromMP ? getTextColor() : headingTextColor}
          />
          <Heading
            as='h5'
            fontSize='lg'
            fontWeight='700'
            color={headingTextColor}
          >
            {categorie?.libelle}
          </Heading>
        </HStack>
      </Box>
      {/* Barre de filtres de catégories, uniquement si on n'est pas déjà dans une ambiance promo ou MP */}
      {filtersHook && !fromPromo && !fromMP && (
        <Box
          display={{base: 'none', md: 'block'}}
          position='relative'
          zIndex={1}
        >
          <CatalogHeaderCategoryFiltersBar
            catWeb={catWeb}
            filtersHook={filtersHook}
            facets={facets}
          />
        </Box>
      )}
      {(fromPromo || fromMP) && (
        <HaliRenderOne
          nav={client}
          render={({data}) => {
            const imageWidth = 1200;
            return (
              <>
                {/* Photo choisie par le serveur selon le métier ou la société ; sans elle, pas d'image de remplacement */}
                {data.url_image_ambiance && (
                  <Image
                    position='absolute'
                    top='0'
                    left='0'
                    width='100%'
                    height='100%'
                    src={imageOptimisee(data.url_image_ambiance, imageWidth, {
                      sans_agrandir: true,
                    })}
                    alt={t('accueil.image_ambiance.alt')}
                    objectFit='cover'
                    objectPosition={
                      data.url_image_ambiance.endsWith(
                        PHOTO_A_CALER_VERS_LE_BAS,
                      )
                        ? 'center 80%'
                        : undefined
                    }
                    zIndex={0}
                  />
                )}
                <Box
                  position='absolute'
                  width='150%'
                  height='60px'
                  bg={fromPromo ? 'red.500' : 'blue.main'}
                  top='50%'
                  left='75%'
                  transform='translate(-50%, -50%) rotate(-35deg)'
                  zIndex={2}
                >
                  <Box
                    position='absolute'
                    top='0'
                    left='0'
                    right='0'
                    bottom='0'
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                    transform='skew(-15deg)'
                  >
                    <Text
                      color='white'
                      fontSize={{base: '2xs', md: 'xl'}}
                      fontWeight='700'
                      letterSpacing='wider'
                      textTransform='uppercase'
                      whiteSpace='nowrap'
                      transform='skew(15deg)'
                    >
                      {fromPromo
                        ? t('catalogue.promotions')
                        : t('catalogue.indispensables')}
                    </Text>
                  </Box>
                </Box>
              </>
            );
          }}
        />
      )}
    </Box>
  );
};
